import { useGetBreeds, useLog } from '@application/api';
import type { Language } from '@application/types';
import { scrollToError, useRouter, useShowToast, useWindowConfirmUnload } from '@application/utils';
import { CountryCode } from '@gql/graphql';
import { MT_LAYOUT_CONTENT_WRAPPER_ID } from '@presentation/components';
import type { MtProfileBreederValues } from '@presentation/pages';
import { MtProfileBreederPage } from '@presentation/pages';
import { PageSkeleton } from '@presentation/pages/MtProfileBreeder/components/PageSkeleton';
import type { BreederFormMode, FormMode } from '@presentation/types';
import { FormikProvider, useFormik } from 'formik';
import type { LocationDescriptor } from 'history';
import hash from 'object-hash';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Navigate } from 'react-router';
import { useValidationSchema } from './useValidationSchema';
import { useGetProfile } from '../MtBreederOnboardingPageController/api/useGetProfile';
import { useProfileInitialValuesBuilders } from '../MtBreederOnboardingPageController/api/useProfileInitialValuesBuilder';
import { useUpdateProfile } from './api/useUpdateProfile';

export const MtProfileBreederPageController: FC = () => {
  const { log } = useLog();
  const { translatedRoute, translate, language, routeLocationDescriptor } = useRouter();
  const { breedOptionsWithoutCrossbreeds } = useGetBreeds();
  const { showToast } = useShowToast();

  const [stateMode, setMode] = useState<BreederFormMode>('view');
  const { profile, profileLoading } = useGetProfile();
  const { updateProfileFromForm, updateProfileLoading } = useUpdateProfile(profile);
  const { mtBreederProfileValidationSchema } = useValidationSchema();

  const translations = useMemo((): Language[] => {
    switch (language) {
      case 'nl':
        return ['nl', 'de', 'en', 'fr'];
      case 'fr':
        return ['fr', 'en', 'nl', 'de'];
      default:
        return ['de', 'nl', 'en', 'fr'];
    }
  }, [language]);

  const { profileInitialValues } = useProfileInitialValuesBuilders(translations, profile);

  const mode: FormMode = useMemo(() => {
    return stateMode;
  }, [stateMode]);

  const formSubmitHandler = useCallback(
    async (values: MtProfileBreederValues) => {
      const changed = hash(values) !== hash(profileInitialValues);

      if (changed) {
        await updateProfileFromForm(values);
      }

      setMode('view');

      if (changed) {
        showToast(translate('common.toasts.title.success'), { type: 'success', autoClose: 1500 });
        document.getElementById(MT_LAYOUT_CONTENT_WRAPPER_ID)?.scrollTo(0, 0);
      }
    },
    [profileInitialValues, showToast, translate, updateProfileFromForm],
  );

  const link = useMemo((): LocationDescriptor | undefined => {
    if (!profile?.dogBreeder?.certified) return;
    const url = profile.dogBreeder.translations?.find((x) => x.language === language)?.url;
    if (!url) return;
    return routeLocationDescriptor('breeder', { params: [url] });
  }, [
    language,
    profile?.dogBreeder?.certified,
    profile?.dogBreeder?.translations,
    routeLocationDescriptor,
  ]);

  const form = useFormik({
    initialValues: profileInitialValues as MtProfileBreederValues,
    validationSchema: mtBreederProfileValidationSchema,
    onSubmit: formSubmitHandler,
    enableReinitialize: true,
  });

  const showCertificationFileField = useMemo(() => {
    const country = profile?.address?.country ?? CountryCode.BE;
    const level = profile?.dogBreeder?.level ?? '';

    return [CountryCode.FR, CountryCode.NL].includes(country) && ['3', '6'].includes(level);
  }, [profile?.address?.country, profile?.dogBreeder?.level]);

  const onSubmitFormHandler = useCallback(async () => {
    if (form.errors) {
      log({
        content: 'MtProfileBreeder error: ',
        params: { errors: form.errors, breederId: profile?.dogBreeder?.id },
      });
    }
    await form.submitForm();
    scrollToError(form.errors);
  }, [form, log, profile?.dogBreeder?.id]);

  const hasFinishedOnboarding = profile?.dogBreeder?.hasFinishedOnboarding;

  useWindowConfirmUnload({ formIsDirty: form?.dirty, formIsSubmitting: form?.isSubmitting });

  if (!hasFinishedOnboarding) {
    return <Navigate replace to={translatedRoute('mtOnboarding')} />;
  }

  if (profileLoading) {
    return <PageSkeleton />;
  }

  return (
    <FormikProvider value={form}>
      <MtProfileBreederPage
        link={link}
        loading={updateProfileLoading}
        options={{ breeds: breedOptionsWithoutCrossbreeds }}
        mode={mode}
        onCancelClick={() => setMode('view')}
        onEditClick={() => setMode('edit')}
        showCertificationFileField={showCertificationFileField}
        onSubmitForm={onSubmitFormHandler}
      />
    </FormikProvider>
  );
};
